import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { BldConditionTypeEnum } from "common/enums/BldConditionTypeEnum";
import { EstateTypeNameEnum } from "common/enums/EstateTypeEnum";
import { calcOld, dateToTermString } from "common/functions/estates";
import { MinimalEstate } from "common/queries/minimalEstates";
import { differenceInDays } from "date-fns";
import React, { useCallback, useMemo } from "react";
import styles from "./ArchivePage.module.scss";

export const EstateList = React.memo(
  (props: {
    estates: MinimalEstate[];
    estateType: string | null | undefined;
    condominiumName: string | null | undefined;
  }) => {
    const groundList = useCallback(
      (estates: MinimalEstate[]) => (
        <TableContainer component={Paper} className={styles.container}>
          <Table className={styles.ground}>
            <TableHead>
              <TableRow>
                <TableCell>所在地</TableCell>
                <TableCell>価格</TableCell>
                <TableCell>広さ</TableCell>
                <TableCell>坪単価</TableCell>
                <TableCell> </TableCell>
                <TableCell>掲載終了</TableCell>
                <TableCell>売出日数</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {estates?.map((x, index) => (
                <TableRow key={`row-${index}`}>
                  <TableCell>{x.fullAddress}</TableCell>
                  <TableCell>
                    {String(Math.round((x?.estatePrice || 0) / 10) * 10)} 万
                  </TableCell>
                  <TableCell>
                    {String((x?.tsuboArea || 0) - ((x?.tsuboArea || 0) % 1))} 坪
                  </TableCell>
                  <TableCell>
                    {String(Math.round((x.unitPrice || 0) * 10) / 10)} 万/坪
                  </TableCell>
                  <TableCell>
                    {x.currentStatus && (
                      <Chip
                        label={x.currentStatus}
                        className={styles.currentStatusLabel}
                      />
                    )}{" "}
                    {x.isLot === 1 && (
                      <Chip label="分譲地" className={styles.lotLabel} />
                    )}{" "}
                    {x.bldConditionType ===
                    BldConditionTypeEnum.建築条件あり ? (
                      <Chip
                        label="建築条件あり"
                        className={styles.currentStatusLabel}
                      />
                    ) : null}
                  </TableCell>
                  <TableCell>{dateToTermString(x.updatedAt)}</TableCell>
                  <TableCell>
                    {x.createdAt &&
                    x.updatedAt &&
                    differenceInDays(x.updatedAt, x.createdAt) > 0
                      ? `${differenceInDays(x.updatedAt, x.createdAt)} 日`
                      : "不明"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ),
      []
    );

    const houseList = useCallback(
      (estates: MinimalEstate[]) => (
        <TableContainer component={Paper} className={styles.container}>
          <Table className={styles.house}>
            <TableHead>
              <TableRow>
                <TableCell>所在地</TableCell>
                <TableCell>価格</TableCell>
                <TableCell>広さ</TableCell>
                <TableCell> </TableCell>
                <TableCell>間取り</TableCell>
                <TableCell>掲載終了</TableCell>
                <TableCell>売出日数</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {estates?.map((x, index) => (
                <TableRow key={`row-${index}`}>
                  <TableCell>{x.fullAddress}</TableCell>
                  <TableCell>
                    {String(Math.round((x?.estatePrice || 0) / 10) * 10)} 万
                  </TableCell>
                  <TableCell>
                    {String((x?.tsuboArea || 0) - ((x?.tsuboArea || 0) % 1))} 坪
                  </TableCell>
                  <TableCell> </TableCell>
                  <TableCell>{x.layout}</TableCell>
                  <TableCell>{dateToTermString(x.updatedAt)}</TableCell>
                  <TableCell>
                    {x.createdAt &&
                    x.updatedAt &&
                    differenceInDays(x.updatedAt, x.createdAt) > 0
                      ? `${differenceInDays(x.updatedAt, x.createdAt)} 日`
                      : "不明"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ),
      []
    );

    const usedList = useCallback(
      (estates: MinimalEstate[]) => (
        <TableContainer component={Paper} className={styles.container}>
          <Table className={styles.used}>
            <TableHead>
              <TableRow>
                <TableCell>所在地</TableCell>
                <TableCell>価格</TableCell>
                <TableCell>広さ</TableCell>
                <TableCell>築年数</TableCell>
                <TableCell>間取り</TableCell>
                <TableCell>掲載終了</TableCell>
                <TableCell>売出日数</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {estates?.map((x, index) => (
                <TableRow key={`row-${index}`}>
                  <TableCell>{x.fullAddress}</TableCell>
                  <TableCell>
                    {String(Math.round((x?.estatePrice || 0) / 10) * 10)} 万
                  </TableCell>
                  <TableCell>
                    {String((x?.tsuboArea || 0) - ((x?.tsuboArea || 0) % 1))} 坪
                  </TableCell>
                  <TableCell>{calcOld(x)} 年</TableCell>
                  <TableCell>{x.layout}</TableCell>
                  <TableCell>{dateToTermString(x.updatedAt)}</TableCell>
                  <TableCell>
                    {x.createdAt &&
                    x.updatedAt &&
                    differenceInDays(x.updatedAt, x.createdAt) > 0
                      ? `${differenceInDays(x.updatedAt, x.createdAt)} 日`
                      : "不明"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ),
      []
    );

    const condominiumList = useCallback(
      (estates: MinimalEstate[]) => (
        <TableContainer component={Paper} className={styles.container}>
          <Table className={styles.condominium}>
            <TableHead>
              <TableRow>
                <TableCell>所在地</TableCell>
                <TableCell>価格</TableCell>
                <TableCell>所在階</TableCell>
                <TableCell>専有面積</TableCell>
                <TableCell>間取り</TableCell>
                <TableCell>掲載終了</TableCell>
                <TableCell>売出日数</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {estates?.map((x, index) => (
                <TableRow key={`row-${index}`}>
                  <TableCell>{x.fullAddress}</TableCell>
                  <TableCell>
                    {String(Math.round((x?.estatePrice || 0) / 10) * 10)} 万
                  </TableCell>
                  <TableCell>{x.floor}階</TableCell>
                  <TableCell>
                    {String((x?.squareArea || 0) - ((x?.squareArea || 0) % 5))}
                    ㎡
                  </TableCell>
                  <TableCell>{x.layout}</TableCell>
                  <TableCell>{dateToTermString(x.updatedAt)}</TableCell>
                  <TableCell>
                    {x.createdAt &&
                    x.updatedAt &&
                    differenceInDays(x.updatedAt, x.createdAt) > 0
                      ? `${differenceInDays(x.updatedAt, x.createdAt)} 日`
                      : "不明"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ),
      []
    );

    const businessList = useCallback(
      (estates: MinimalEstate[]) => (
        <TableContainer component={Paper} className={styles.container}>
          <Table className={styles.used}>
            <TableHead>
              <TableRow>
                <TableCell>所在地</TableCell>
                <TableCell>価格</TableCell>
                <TableCell>広さ</TableCell>
                <TableCell>築年数</TableCell>
                <TableCell>種目</TableCell>
                <TableCell>掲載終了</TableCell>
                <TableCell>売出日数</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {estates?.map((x, index) => (
                <TableRow key={`row-${index}`}>
                  <TableCell>{x.fullAddress}</TableCell>
                  <TableCell>
                    {String(Math.round((x?.estatePrice || 0) / 10) * 10)} 万
                  </TableCell>
                  <TableCell>
                    {String((x?.tsuboArea || 0) - ((x?.tsuboArea || 0) % 1))} 坪
                  </TableCell>
                  <TableCell>{calcOld(x)} 年</TableCell>
                  <TableCell>{x.purposeB}</TableCell>
                  <TableCell>{dateToTermString(x.updatedAt)}</TableCell>
                  <TableCell>
                    {x.createdAt &&
                    x.updatedAt &&
                    differenceInDays(x.updatedAt, x.createdAt) > 0
                      ? `${differenceInDays(x.updatedAt, x.createdAt)} 日`
                      : "不明"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ),
      []
    );

    const filteredEstates = useMemo(
      () => props.estates.filter((x) => x.estateType === props.estateType),
      [props.estateType, props.estates]
    );

    filteredEstates.sort(
      (a, b) =>
        (b.updatedAt === undefined || b.updatedAt === null
          ? Number.MIN_VALUE
          : new Date(b.updatedAt).getTime()) -
          (a.updatedAt === undefined || a.updatedAt === null
            ? Number.MIN_VALUE
            : new Date(a.updatedAt).getTime()) ||
        (a.estatePrice || 0) - (b.estatePrice || 0) ||
        (b.recNo || 0) - (a.recNo || 0)
    );

    return props.estateType === EstateTypeNameEnum.土地 ||
      props.estateType === EstateTypeNameEnum.分譲地
      ? groundList(filteredEstates)
      : props.estateType === EstateTypeNameEnum.新築建売
      ? houseList(filteredEstates)
      : props.estateType === EstateTypeNameEnum.中古戸建
      ? usedList(filteredEstates)
      : props.estateType === EstateTypeNameEnum.マンション
      ? condominiumList(filteredEstates)
      : props.estateType === EstateTypeNameEnum.事業用
      ? businessList(filteredEstates)
      : null;
  }
);

export const Styler = () => (
  <style>{`
        @media print {
          @page {
            size: A4 portrait !important;
            margin: 0mm 5mm;
          }
        }
    `}</style>
);
